/** 分组编辑弹窗：创建 / 改名 / 增删成员。 */

import { useEffect, useState } from "react";

import {
  createGroup,
  deleteGroup,
  patchGroup,
  type Group,
  type GroupMember,
} from "@/api/groups";
import { ApiError } from "@/api/client";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toast } from "@/components/Toast";
import { chooseContact } from "@/feishu/jssdk";

interface Props {
  group: Group | null;      // null = 新建
  onClose: () => void;
  onSaved: (g: Group | null) => void;  // null 表示已删除
}

export function GroupEditor({ group, onClose, onSaved }: Props) {
  const isNew = group === null;
  const [name, setName] = useState(group?.name ?? "");
  const [members, setMembers] = useState<GroupMember[]>(group?.members ?? []);
  const [busy, setBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setName(group?.name ?? "");
    setMembers(group?.members ?? []);
  }, [group]);

  const addFromFeishu = async () => {
    const picked = await chooseContact({ multi: true, limit: 50 });
    if (picked.length === 0) {
      toast.info("未选择任何联系人");
      return;
    }
    setMembers((prev) => {
      const map = new Map(prev.map((m) => [m.open_id, m]));
      for (const p of picked) {
        map.set(p.open_id, { open_id: p.open_id, name: p.name });
      }
      return [...map.values()];
    });
  };

  const removeMember = (open_id: string) =>
    setMembers((prev) => prev.filter((m) => m.open_id !== open_id));

  const save = async () => {
    if (!name.trim()) {
      toast.error("分组名不能为空");
      return;
    }
    setBusy(true);
    try {
      let saved: Group;
      if (isNew) {
        saved = await createGroup(name.trim(), members);
      } else {
        // 简化：把当前 members 全部 upsert；移除目标是原成员里不在当前列表的
        const originalIds = new Set(group!.members.map((m) => m.open_id));
        const currentIds = new Set(members.map((m) => m.open_id));
        const toRemove = [...originalIds].filter((id) => !currentIds.has(id));
        saved = await patchGroup(group!.id, {
          name: name.trim() === group!.name ? undefined : name.trim(),
          add_members: members,
          remove_member_open_ids: toRemove.length > 0 ? toRemove : undefined,
        });
      }
      toast.success(isNew ? "分组已创建" : "分组已更新");
      onSaved(saved);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!group) return;
    setShowDeleteConfirm(true);
  };

  const confirmRemove = async () => {
    setShowDeleteConfirm(false);
    setBusy(true);
    try {
      await deleteGroup(group!.id);
      toast.success("已删除");
      onSaved(null);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "删除失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {isNew ? "新建分组" : "编辑分组"}
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            分组名
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
            placeholder="例如：前端组"
          />
        </label>

        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">
              成员 ({members.length})
            </span>
            <button
              onClick={addFromFeishu}
              className="rounded-full bg-sky-600 px-3 py-1 text-xs text-white hover:bg-sky-700"
            >
              从通讯录添加
            </button>
          </div>
          {members.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-400">
              还没有成员
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {members.map((m) => (
                <span
                  key={m.open_id}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
                >
                  {m.name ?? m.open_id}
                  <button
                    onClick={() => removeMember(m.open_id)}
                    className="text-slate-400 hover:text-slate-700"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div>
            {!isNew ? (
              <button
                onClick={remove}
                disabled={busy}
                className="rounded-full px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
              >
                删除分组
              </button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              取消
            </button>
            <button
              onClick={save}
              disabled={busy}
              className="rounded-full bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700 disabled:bg-slate-300"
            >
              {busy ? "保存中…" : "保存"}
            </button>
          </div>
        </div>
      </div>
      {showDeleteConfirm ? (
        <ConfirmDialog
          title="删除分组"
          message={`确认删除分组「${group!.name}」？已共享的历史数据不受影响。`}
          confirmText="删除"
          danger
          onConfirm={confirmRemove}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      ) : null}
    </div>
  );
}
